import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, BookOpen, Database, Play, Sliders } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useSessionSwitcher } from "@/contexts/SessionSwitcherContext";
import { useGuideProgress } from "@/hooks/api/query/useGuideProgress";
import { isRiskAnalysisUnlocked } from "@/utils/guideProgressCalculator";

const RISK_COLORS = {
  green: "#22c55e", // BASIC (scores 1-7)
  yellow: "#eab308", // IMPORTANT (scores 8-14)
  red: "#ef4444", // ESENȚIAL (scores 15-25)
};

const RISK_MATRIX = {
  impactLevels: ["Minim", "Scăzut", "Mediu", "Ridicat", "Critic"],
  probabilityLevels: [
    "F. Improbabil",
    "Improbabil",
    "Posibil",
    "Probabil",
    "F. Probabil",
  ],
  // Colors based on score = impact × probability
  // Row 0 = prob 5 (scores: 5,10,15,20,25)
  // Row 4 = prob 1 (scores: 1,2,3,4,5)
  colors: [
    [
      RISK_COLORS.green,
      RISK_COLORS.yellow,
      RISK_COLORS.red,
      RISK_COLORS.red,
      RISK_COLORS.red,
    ], // prob=5
    [
      RISK_COLORS.green,
      RISK_COLORS.yellow,
      RISK_COLORS.yellow,
      RISK_COLORS.red,
      RISK_COLORS.red,
    ], // prob=4
    [
      RISK_COLORS.green,
      RISK_COLORS.green,
      RISK_COLORS.yellow,
      RISK_COLORS.yellow,
      RISK_COLORS.red,
    ], // prob=3
    [
      RISK_COLORS.green,
      RISK_COLORS.green,
      RISK_COLORS.green,
      RISK_COLORS.yellow,
      RISK_COLORS.yellow,
    ], // prob=2
    [
      RISK_COLORS.green,
      RISK_COLORS.green,
      RISK_COLORS.green,
      RISK_COLORS.green,
      RISK_COLORS.green,
    ], // prob=1
  ],
};

const getRiskLevel = (impact: number, prob: number) => {
  const score = impact * prob;
  if (score >= 15)
    return { level: "ESENȚIAL", color: "text-red-700 bg-red-100" };
  if (score >= 8)
    return { level: "IMPORTANT", color: "text-amber-700 bg-amber-100" };
  return { level: "de BAZĂ", color: "text-emerald-700 bg-emerald-100" };
};

const GhidAnalizaRisc = () => {
  const navigate = useNavigate();
  const { currentSessionId } = useSessionSwitcher();
  const { entityData } = useGuideProgress(currentSessionId);
  const isNotificareCompleted = isRiskAnalysisUnlocked(entityData);
  const [selectedCell, setSelectedCell] = useState({ impact: 3, prob: 3 });

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-5xl mx-auto">
        <div className="mb-4 md:mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="md:w-12 w-10 md:h-12 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
              <BookOpen size={24} className="text-primary" />
            </div>
            <div>
              <h1 className="font-heading md:text-2xl text-xl font-bold text-slate-800">
                Ghid Analiză de Risc
              </h1>
              <p className="md:text-sm text-xs text-slate-600">
                Metodologia ENIRE@RO
              </p>
            </div>
          </div>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="md:text-lg text-base">
              Ce este Analiza de Risc?
            </CardTitle>
            <CardDescription className="md:text-sm text-xs">
              Cadrul metodologic pentru evaluarea riscurilor cibernetice
            </CardDescription>
          </CardHeader>
          <CardContent className="md:text-sm text-xs pt-0 md:pt-0">
            <p className="text-slate-600 mb-4">
              Este procesul prin care determini cât de vulnerabil ești în fața a
              5 tipuri de atacuri (ex: DDoS, Ransomware) executate de 5 tipuri
              de actori (de la Hacktiviști la Actori Statali).
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <h4 className="font-heading font-medium md:text-sm text-xs text-blue-800 mb-2">
                  Tipuri de Atacuri
                </h4>
                <ul className="md:text-sm text-xs text-blue-700 space-y-1">
                  <li>• DDoS (Distributed Denial of Service)</li>
                  <li>• Ransomware</li>
                  <li>• Phishing / Social Engineering</li>
                  <li>• Exploatare vulnerabilități</li>
                  <li>• Acces neautorizat</li>
                </ul>
              </div>
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <h4 className="font-heading font-medium md:text-sm text-xs text-blue-800 mb-2">
                  Tipuri de Actori
                </h4>
                <ul className="md:text-sm text-xs text-blue-700 space-y-1">
                  <li>• Hacktiviști</li>
                  <li>• Criminali cibernetici</li>
                  <li>• Insideri (angajați)</li>
                  <li>• Competitori</li>
                  <li>• Actori statali (APT)</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="md:text-lg text-base">
              Matricea de Risc (Impact × Probabilitate)
            </CardTitle>
            <CardDescription className="md:text-sm text-xs">
              Selectați o celulă pentru a vedea nivelul de risc
            </CardDescription>
          </CardHeader>
          <CardContent className="md:text-sm text-xs pt-0 md:pt-0">
            <div className="flex gap-8 flex-wrap">
              <div className="flex-1 min-w-[300px]">
                <div className="flex">
                  <div className="md:w-24 w-20" />
                  <div className="flex-1 grid grid-cols-5 gap-1 mb-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div
                        key={i}
                        className="text-center text-xs font-medium text-slate-600"
                      >
                        {i}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex items-center mb-1">
                  <div className="md:w-24 w-20" />
                  <p className="flex-1 text-center text-xs text-slate-500">
                    ← Impact →
                  </p>
                </div>

                {[5, 4, 3, 2, 1].map((prob) => (
                  <div key={prob} className="flex items-center gap-1 mb-1">
                    <div className="md:w-24 w-20 text-right pr-2">
                      <span className="text-xs text-slate-600">
                        {RISK_MATRIX.probabilityLevels[prob - 1]}
                      </span>
                    </div>
                    {[1, 2, 3, 4, 5].map((impact) => {
                      const isSelected =
                        selectedCell.impact === impact &&
                        selectedCell.prob === prob;
                      const cellColor =
                        RISK_MATRIX.colors[5 - prob][impact - 1];
                      return (
                        <button
                          key={impact}
                          onClick={() => setSelectedCell({ impact, prob })}
                          className={`flex-1 h-12 rounded-lg flex items-center justify-center text-sm font-bold transition-all ${
                            isSelected ? "scale-105" : "hover:scale-105"
                          }`}
                          style={{
                            backgroundColor: cellColor,
                            ...(isSelected && {
                              outline: "2px solid",
                              outlineColor: cellColor,
                              outlineOffset: "2px",
                            }),
                          }}
                        >
                          <span className="text-white drop-shadow">
                            {impact * prob}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ))}

                <div className="flex items-center mt-2">
                  <div className="md:w-24 w-20 text-right pr-2 text-xs text-slate-500">
                    Probabilitate ↑
                  </div>
                </div>
              </div>

              <div className="w-full md:w-64 space-y-4 grid grid-cols-2 md:grid-cols-1 gap-4">
                <div className="p-3 md:p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <h4 className="font-heading font-medium text-blue-800 mb-2">
                    Celulă Selectată
                  </h4>
                  <p className="md:text-sm text-xs text-blue-700">
                    Impact: <strong>{selectedCell.impact}</strong>
                  </p>
                  <p className="md:text-sm text-xs text-blue-700">
                    Probabilitate: <strong>{selectedCell.prob}</strong>
                  </p>
                  <p className="md:text-sm text-xs text-blue-700">
                    Scor:{" "}
                    <strong>{selectedCell.impact * selectedCell.prob}</strong>
                  </p>
                  <div className="mt-2">
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-bold ${
                        getRiskLevel(selectedCell.impact, selectedCell.prob)
                          .color
                      }`}
                    >
                      {
                        getRiskLevel(selectedCell.impact, selectedCell.prob)
                          .level
                      }
                    </span>
                  </div>
                </div>

                <div className="grid  gap-2 content-start">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-6 h-6 rounded"
                      style={{ backgroundColor: RISK_COLORS.green }}
                    />
                    <span className="md:text-sm text-xs text-blue-700">
                      1-7: BASIC
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-6 h-6 rounded"
                      style={{ backgroundColor: RISK_COLORS.yellow }}
                    />
                    <span className="md:text-sm text-xs text-blue-700">
                      8-14: IMPORTANT
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-6 h-6 rounded"
                      style={{ backgroundColor: RISK_COLORS.red }}
                    />
                    <span className="md:text-sm text-xs text-blue-700">
                      15-25: ESENȚIAL
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="md:text-lg text-base">
              Cum funcționează în NIS2PASS
            </CardTitle>
            <CardDescription className="md:text-sm text-xs">
              Workflow-ul aplicației pentru analiza de risc
            </CardDescription>
          </CardHeader>
          <CardContent className="md:text-sm text-xs pt-0 md:pt-0">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <h4 className="font-heading font-medium md:text-sm text-xs text-blue-800 mb-2 flex items-center gap-2">
                  <Database size={18} />
                  Valorile Default
                </h4>
                <p className="md:text-sm text-xs text-blue-700">
                  Aplicația preîncarcă valorile stabilite de DNSC pentru
                  sectorul tău.
                </p>
              </div>
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <h4 className="font-heading font-medium md:text-sm text-xs text-blue-800 mb-2 flex items-center gap-2">
                  <Sliders size={18} />
                  Modificarea Parametrilor
                </h4>
                <p className="md:text-sm text-xs text-blue-700">
                  Poți ajusta Impactul (1-5) și Probabilitatea (1-5).
                </p>
              </div>
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <h4 className="font-heading font-medium md:text-sm text-xs text-blue-800 mb-2 flex items-center gap-2">
                  <AlertCircle size={18} />
                  Cerința de Motivare
                </h4>
                <p className="md:text-sm text-xs text-blue-700">
                  Dacă modifici valoarea default, ești obligat să introduci o
                  „Motivare Temeinică".
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl md:p-6 p-4 text-white">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h3 className="font-heading font-bold md:text-xl text-lg">Rezultat</h3>
              <p className="text-blue-100 md:text-sm text-xs mt-1">
                Scorul agregat determină dacă trebuie să aplici măsuri de
                securitate de nivel BASIC, IMPORTANT sau ESENȚIAL.
              </p>
              <p className="text-blue-200 md:text-sm text-xs mt-2">
                Output: Raport Analiză de Risc PDF
              </p>
            </div>
            <button
              onClick={() =>
                isNotificareCompleted &&
                currentSessionId &&
                navigate(`/analiza-risc/${currentSessionId}`)
              }
              disabled={!currentSessionId || !isNotificareCompleted}
              className={`md:px-6 px-4 md:py-3 py-2 md:text-sm text-xs bg-white text-blue-600 font-medium rounded-xl flex items-center gap-2 ${
                isNotificareCompleted && currentSessionId
                  ? "hover:bg-blue-50"
                  : "opacity-50 cursor-not-allowed"
              }`}
            >
              {isNotificareCompleted && currentSessionId ? (
                <>
                  <Play size={18} />
                  Start Modul
                </>
              ) : (
                "Finalizați formularul de notificare pentru a accesa acest modul"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GhidAnalizaRisc;
